// CompanyRelationList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
} from '@mui/icons-material';

const CompanyRelationList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [relations, setRelations] = useState([]);
  const [filteredRelations, setFilteredRelations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('companyName');

  // Filter state for each column
  const [filters, setFilters] = useState({
    companyName: '',
    relativeCompanyName: '',
    relativeCompanyCountry: '',
    isActive: '',
  });

  useEffect(() => {
    fetchRelations();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [relations, filters]);

  const fetchRelations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/CompanyRelations');
      console.log('Relations data:', response.data);
      setRelations(response.data || []);
    } catch (error) {
      console.error('Error fetching relations:', error);
      setError('Failed to load company relations. Please try again.');
      setRelations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...relations];

    // Filter by company name - starts with
    if (filters.companyName) {
      const search = filters.companyName.toLowerCase();
      filtered = filtered.filter((relation) => {
        const companyName = (relation.companyName || '').toLowerCase();
        return companyName.startsWith(search);
      });
    }

    // Filter by relative company name - starts with
    if (filters.relativeCompanyName) {
      const search = filters.relativeCompanyName.toLowerCase();
      filtered = filtered.filter((relation) => {
        const relativeName = (relation.relativeCompanyName || '').toLowerCase();
        return relativeName.startsWith(search);
      });
    }

    // Filter by country - starts with
    if (filters.relativeCompanyCountry) {
      const search = filters.relativeCompanyCountry.toLowerCase();
      filtered = filtered.filter((relation) => {
        const country = (relation.relativeCompanyCountry || '').toLowerCase();
        return country.startsWith(search);
      });
    }

    // Filter by active status
    if (filters.isActive !== '') {
      const isActive = filters.isActive === 'true';
      filtered = filtered.filter((relation) => relation.isActive === isActive);
    }

    setFilteredRelations(filtered);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    navigate('/relations/create');
  };

  const handleEdit = (id) => {
    navigate(`/relations/edit/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this company relation?')) {
      try {
        await api.delete(`/CompanyRelations/${id}`);
        fetchRelations();
      } catch (error) {
        console.error('Error deleting relation:', error);
        setError('Failed to delete relation. Please try again.');
      }
    }
  };

  const handleRefresh = () => {
    fetchRelations();
    setFilters({
      companyName: '',
      relativeCompanyName: '',
      relativeCompanyCountry: '',
      isActive: '',
    });
    setOrder('asc');
    setOrderBy('companyName');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort function
  const sortData = (data, orderBy, order) => {
    return [...data].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle boolean comparison
      if (typeof aValue === 'boolean') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      }
    });
  };

  // Apply sorting to filtered data
  const sortedData = sortData(filteredRelations, orderBy, order);

  // Pagination
  const paginatedRelations = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Render filter row
  const renderFilterRow = () => (
    <TableRow sx={{ bgcolor: '#fafafa' }}>
      <TableCell sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Filter company"
          value={filters.companyName}
          onChange={handleFilterChange('companyName')}
          fullWidth
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Filter relative"
          value={filters.relativeCompanyName}
          onChange={handleFilterChange('relativeCompanyName')}
          fullWidth
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Filter country"
          value={filters.relativeCompanyCountry}
          onChange={handleFilterChange('relativeCompanyCountry')}
          fullWidth
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ p: 1, width: '150px' }}>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.isActive}
            onChange={handleFilterChange('isActive')}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ p: 1 }} align="center">
        <Tooltip title="Clear Filters">
          <IconButton
            size="small"
            onClick={() => setFilters({
              companyName: '',
              relativeCompanyName: '',
              relativeCompanyCountry: '',
              isActive: '',
            })}
          >
            ✕
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3, maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Company Relations
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton onClick={handlePrint} color="primary">
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{ ml: 1 }}
            >
              New Relation
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Table */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'companyName'}
                        direction={orderBy === 'companyName' ? order : 'asc'}
                        onClick={() => handleSort('companyName')}
                      >
                        Company
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'relativeCompanyName'}
                        direction={orderBy === 'relativeCompanyName' ? order : 'asc'}
                        onClick={() => handleSort('relativeCompanyName')}
                      >
                        Relative Company
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'relativeCompanyCountry'}
                        direction={orderBy === 'relativeCompanyCountry' ? order : 'asc'}
                        onClick={() => handleSort('relativeCompanyCountry')}
                      >
                        Country
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      <TableSortLabel
                        active={orderBy === 'isActive'}
                        direction={orderBy === 'isActive' ? order : 'asc'}
                        onClick={() => handleSort('isActive')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                  {/* Filter Row */}
                  {renderFilterRow()}
                </TableHead>
                <TableBody>
                  {paginatedRelations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                          No company relations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRelations.map((relation) => (
                      <TableRow key={relation.relationID} hover>
                        <TableCell>
                          <Chip 
                            label={relation.companyName || 'N/A'} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={relation.relativeCompanyName || 'N/A'} 
                            size="small" 
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={relation.relativeCompanyCountry || 'N/A'} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={relation.isActive ? 'Active' : 'Inactive'}
                            color={relation.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(relation.relationID)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(relation.relationID)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredRelations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CompanyRelationList;